import { EyeOutlined, HistoryOutlined } from "@ant-design/icons";
import {
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Button,
  Modal,
  Table,
  Tooltip,
} from "antd";
import { formatNumberWithCommas } from "utils/functions";
import { hasPermission } from "../../../utils/functions";
import { CM_VALUE_APPROVE_PERMISSION } from "../../../routes/permissions";
import { useState, useEffect } from "react";
import moment from "moment";
import UploadCMDoc from "./UploadCMDoc";

const CMForm = (props) => {
  const {
    inquiryId,
    viewMode,
    approvalMode = false,
    form,
    histories,
    filesList,
    setFileList,
    onCMFieldsChange,
    CMInfo,
    editInfo,
    cmFieldsChanged,
  } = props;

  const isAlreadyApproved = editInfo?.cm_status === 1;
  const canApproveCM = hasPermission([CM_VALUE_APPROVE_PERMISSION]);

  const isCmFieldDisabled = (fieldName) => {
    if (fieldName === "cgl_target" && isAlreadyApproved) {
      return true;
    }

    if (fieldName === "expected_margin") {
      if (approvalMode) {
        return isAlreadyApproved || !canApproveCM;
      }
      return true;
    }

    if (fieldName === "negotiated_cm") {
      if (approvalMode) {
        return !canApproveCM;
      }
      return true;
    }

    if (approvalMode) {
      return true;
    }

    const alwaysDisabled = [
      "breakeven_cm",
      "total_expected_margin",
      "difference",
      "operational_profit",
    ];
    if (alwaysDisabled.includes(fieldName)) {
      return true;
    }
    return viewMode;
  };

  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);

  const toggleHistoryModal = () => {
    setIsHistoryModalVisible(!isHistoryModalVisible);
  };

  const toCMShowPage = () => {
    window.open(`/cost-of-making/details/${inquiryId}`);
  };

  const checkForChanges = () => {
    if (form && onCMFieldsChange && editInfo) {
      const currentExpectedMargin = form.getFieldValue("expected_margin");
      const currentTotalExpectedMargin = form.getFieldValue(
        "total_expected_margin",
      );
      const currentBuyerTarget = form.getFieldValue("buyer_target");
      const currentNegotiatedCm = form.getFieldValue("negotiated_cm");
      const currentCglTarget = form.getFieldValue("cgl_target");

      const hasChanged =
        Number(currentExpectedMargin || 0) !==
        Number(editInfo.expected_margin || 0) ||
        Number(parseFloat(currentBuyerTarget || 0).toFixed(2)) !==
        Number(parseFloat(editInfo.buyer_target || 0).toFixed(2)) ||
        Number(parseFloat(currentNegotiatedCm || 0).toFixed(2)) !==
        Number(parseFloat(editInfo.negotiated_cm || 0).toFixed(2)) ||
        Number(parseFloat(currentCglTarget || 0).toFixed(2)) !==
        Number(parseFloat(editInfo.quoted_cm || 0).toFixed(2));

      onCMFieldsChange(hasChanged);
    }
  };

  const historyColumns = [
    {
      title: "Expected Margin %",
      dataIndex: "expected_margin",
      key: "expected_margin",
    },
    {
      title: "Total Expected Margin $",
      dataIndex: "total_expected_margin",
      key: "total_expected_margin",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Buyer Target $",
      dataIndex: "buyer_target",
      key: "buyer_target",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Negotiated CM",
      dataIndex: "negotiated_cm",
      key: "negotiated_cm",
    },
    {
      title: "Difference $",
      dataIndex: "difference",
      key: "difference",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Operation Profit",
      dataIndex: "operation_profit",
      key: "operation_profit",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "Uploaded Document",
      dataIndex: "document_name",
      key: "document_name",
      render: (text, record) => {
        return (
          <a
            href={`${process.env.REACT_APP_BASE_URL}/${record.document}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            "{text}
          </a>
        );
      },
    },
    {
      title: "Updated By",
      dataIndex: ["created_by", "name"],
      key: "updater",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (text) => moment(text).format("DD-MMM-YYYY HH:mm"),
    },
  ];

  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              <span style={{ fontSize: "14px" }}>CM</span>
              <span
                style={{ fontSize: "15px", cursor: "pointer" }}
                onClick={toCMShowPage}
              >
                <EyeOutlined />
              </span>
            </div>
            {histories && histories.length > 0 && (
              <Tooltip title="See History">
                <Button
                  type="primary"
                  icon={<HistoryOutlined />}
                  onClick={toggleHistoryModal}
                  size="small"
                  style={{ alignItems: "end" }}
                >
                  See History
                </Button>
              </Tooltip>
            )}
          </div>
        }
      >
        <Row gutter={6}>
          <Col span={4}>
            <Form.Item label="Breakeven CM $" name="breakeven_cm">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Breakeven CM $"
                size="small"
                disabled={isCmFieldDisabled("breakeven_cm")}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Expected Profit Percentage"
              name="expected_margin"
              normalize={(value) => {
                if (value === "" || value === null || value === undefined) {
                  return null;
                }
                const num = Number(value);
                return Number.isNaN(num) ? null : Math.trunc(num);
              }}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Expected Profit %"
                size="small"
                disabled={isCmFieldDisabled("expected_margin")}
                precision={0}
                controls={false}
                parser={(value) => {
                  const cleaned = String(value ?? "").replace(/\D/g, "");
                  return cleaned === "" ? "" : Number(cleaned);
                }}
                onChange={(value) => {
                  form.setFieldsValue({
                    expected_margin:
                      value === 0 || value === null || value === undefined
                        ? null
                        : Math.trunc(Number(value)),
                  });
                  checkForChanges();
                }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Total Expected Margin $"
              name="total_expected_margin"
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Total Expected Margin $"
                size="small"
                disabled={isCmFieldDisabled("total_expected_margin")}
                precision={4}
                formatter={(value) => formatNumberWithCommas(value)}
                onChange={checkForChanges}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Quoted CM" name="cgl_target">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Quoted CM"
                size="small"
                disabled={isCmFieldDisabled("cgl_target")}
                precision={2}
                formatter={(value) => formatNumberWithCommas(value)}
                onChange={checkForChanges}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Buyer Target $" name="buyer_target">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Buyer Target $"
                size="small"
                disabled={isCmFieldDisabled("buyer_target")}
                onChange={checkForChanges}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Negotiated CM" name="negotiated_cm">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Negotiated CM"
                size="small"
                disabled={isCmFieldDisabled("negotiated_cm")}
                onChange={checkForChanges}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={6}>
          <Col span={4}>
            <Form.Item label="Difference $" name="difference">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Difference $"
                size="small"
                disabled
                precision={2}
                formatter={(value) => formatNumberWithCommas(value)}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Operational Profit $" name="operational_profit">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Operational Profit $"
                size="small"
                disabled
                precision={2}
                formatter={(value) => formatNumberWithCommas(value)}
              />
            </Form.Item>
          </Col>

          <Col span={16}>
            {(isAlreadyApproved || !approvalMode) && (
              <UploadCMDoc
                DOC_ADD={`/api/buyer-inquiries/store-cm-histories`}
                setFileList={setFileList}
                filesList={filesList}
                view={approvalMode || viewMode || isAlreadyApproved}
                // Keep CM document upload optional for Buyer Inquiry.
                // showUploadRequired={
                //   !viewMode &&
                //   cmFieldsChanged &&
                //   !(Array.isArray(filesList) && filesList.length > 0 && filesList[0]?.id)
                // }
                showUploadRequired={false}
                onChange={() => {
                  checkForChanges();
                }}
              />
            )}
          </Col>
        </Row>
      </Card>

      {/* History Modal */}
      <Modal
        title="CM History"
        open={isHistoryModalVisible}
        onCancel={toggleHistoryModal}
        width="80%"
        footer={[
          <Button key="close" onClick={toggleHistoryModal}>
            Close
          </Button>,
        ]}
      >
        <Table
          dataSource={histories || []}
          columns={historyColumns}
          rowKey="id"
          pagination={false}
          scroll={{ x: "max-content" }}
          size="small"
        />
      </Modal>
    </>
  );
};

export default CMForm;
